import React, { useEffect, useState } from 'react';
import { hasSupabaseConfig, supabase } from './supabaseClient';
import { PRIVACY_POLICY_CONTENT, TERMS_OF_SERVICE_CONTENT } from './legalContent';

const defaultAlert = { type: 'info', message: '' };
const USERNAME_REGEX = /^[a-z0-9_]+$/;
const appSiteUrl = (process.env.REACT_APP_SITE_URL || '').trim().replace(/\/+$/, '');

const getFallbackProfile = (user) => ({
  id: user.id,
  email: user.email || '',
  username: (user.email || '').split('@')[0] || 'user',
});

const getSiteOrigin = () => {
  if (appSiteUrl) return appSiteUrl;
  if (typeof window !== 'undefined') return window.location.origin;
  return 'https://unlockapp.me';
};

const validateUsername = (rawUsername) => {
  const value = rawUsername ?? '';

  if (!value.trim()) {
    return 'Username is required.';
  }

  if (value !== value.trim()) {
    return 'Username cannot start or end with spaces.';
  }

  if (/\s/.test(value)) {
    return 'Username cannot contain spaces.';
  }

  if (/[A-Z]/.test(value)) {
    return 'Username must use lowercase letters only (capital letters are not allowed).';
  }

  if (value.length < 2 || value.length > 20) {
    return 'Username must be between 2 and 20 characters.';
  }

  if (!USERNAME_REGEX.test(value)) {
    return 'Username can only contain lowercase letters, numbers, and underscores (_).';
  }

  return null;
};

const validatePassword = (password, confirmPassword) => {
  if (!password) {
    return 'Password is required.';
  }

  if (!confirmPassword) {
    return 'Please confirm your password.';
  }

  if (password !== confirmPassword) {
    return 'Passwords do not match.';
  }

  if (/\s/.test(password)) {
    return 'Password cannot contain spaces.';
  }

  if (password.length < 6) {
    return 'Password must be at least 6 characters.';
  }

  return null;
};

function AuthModal({ isOpen, onClose, onLoginSuccess }) {
  const [isSignup, setIsSignup] = useState(false);
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [gender, setGender] = useState('male');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [termsModalVisible, setTermsModalVisible] = useState(false);
  const [termsTab, setTermsTab] = useState('tos');
  const [isLoading, setIsLoading] = useState(false);
  const [alert, setAlert] = useState(defaultAlert);

  useEffect(() => {
    if (!isOpen) {
      setIsLoading(false);
      setAlert(defaultAlert);
      setTermsModalVisible(false);
      setTermsTab('tos');
      return;
    }
    setPassword('');
    setConfirmPassword('');
    setAlert(defaultAlert);
  }, [isOpen]);

  if (!isOpen) return null;

  const showAlert = (message, type = 'error') => {
    setAlert({ message, type });
  };

  const fetchUserData = async (user) => {
    try {
      const { data, error } = await supabase
        .from('credentials')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      if (error) {
        return getFallbackProfile(user);
      }

      return data || getFallbackProfile(user);
    } catch (error) {
      return getFallbackProfile(user);
    }
  };

  const handleLogin = async (event) => {
    event.preventDefault();

    if (!email || !password) {
      showAlert('Please enter email and password');
      return;
    }

    if (!hasSupabaseConfig) {
      showAlert('Missing Supabase configuration.');
      return;
    }

    setIsLoading(true);
    setAlert(defaultAlert);

    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password,
      });

      if (error) {
        showAlert(error.message);
        return;
      }

      if (data?.user) {
        const profile = await fetchUserData(data.user);
        onLoginSuccess(profile);
        onClose();
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Login error:', error);
      showAlert('An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      showAlert('Please enter a valid email address.');
      return;
    }

    if (!hasSupabaseConfig) {
      showAlert('Missing Supabase configuration.');
      return;
    }

    setIsLoading(true);
    setAlert(defaultAlert);

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        showAlert(error.message);
        return;
      }

      showAlert(
        'We sent a password reset link to your email. Check inbox and spam.',
        'success'
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Forgot password error:', error);
      showAlert('Something went wrong.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleAuth = async () => {
    if (!hasSupabaseConfig) {
      showAlert('Missing Supabase configuration.');
      return;
    }

    setIsLoading(true);
    setAlert(defaultAlert);

    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${getSiteOrigin()}/app`,
        },
      });

      if (error) {
        showAlert(error.message || 'Google sign-in failed. Please try again.');
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Google auth error:', error);
      showAlert('Google sign-in failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignup = async (event) => {
    event.preventDefault();

    const cleanUsername = username.trim();
    const cleanEmail = email.trim().toLowerCase();

    if (!cleanUsername || !cleanEmail || !password || !confirmPassword) {
      showAlert('Please fill in all fields');
      return;
    }

    const usernameError = validateUsername(cleanUsername);
    if (usernameError) {
      showAlert(usernameError);
      return;
    }

    const passwordError = validatePassword(password, confirmPassword);
    if (passwordError) {
      showAlert(passwordError);
      return;
    }

    if (!agreeToTerms) {
      showAlert('You must agree to the Terms of Service and Privacy Policy');
      return;
    }

    if (!hasSupabaseConfig) {
      showAlert('Missing Supabase configuration.');
      return;
    }

    setIsLoading(true);
    setAlert(defaultAlert);

    try {
      const { data: existingUser, error: checkError } = await supabase
        .from('credentials')
        .select('username')
        .eq('username', cleanUsername)
        .maybeSingle();

      if (checkError) {
        showAlert('Failed to validate username. Please try again.');
        return;
      }

      if (existingUser) {
        showAlert('Username already exists');
        return;
      }

      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: cleanEmail,
        password,
        options: {
          emailRedirectTo: `${getSiteOrigin()}/email-confirmation`,
        },
      });

      if (authError) {
        showAlert(authError.message);
        return;
      }

      if (!authData.user) {
        showAlert('No user data returned');
        return;
      }

      if (Array.isArray(authData.user.identities) && authData.user.identities.length === 0) {
        showAlert('This email is already registered.');
        return;
      }

      const { error: profileError } = await supabase.from('credentials').insert([
        {
          id: authData.user.id,
          username: cleanUsername,
          email: cleanEmail,
          gender,
          user_rate: 100,
          chat_preference: 'both',
          avatar_url: null,
          user_rating: null,
          user_instapay: null,
        },
      ]);

      if (profileError) {
        showAlert('Failed to create user profile');
        return;
      }

      showAlert(
        'Account created. Check your inbox and spam folder to confirm your email before logging in.',
        'success'
      );
      setIsSignup(false);
      setUsername('');
      setPassword('');
      setConfirmPassword('');
      setGender('male');
      setAgreeToTerms(false);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Signup error:', error);
      showAlert('An error occurred during signup');
    } finally {
      setIsLoading(false);
    }
  };

  const onSubmit = isSignup ? handleSignup : handleLogin;
  const openLegalModal = (tab) => {
    setTermsTab(tab);
    setTermsModalVisible(true);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content auth-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="auth-close-btn" onClick={onClose} type="button" aria-label="Close">
          <i className="fas fa-times" />
        </button>

        <h3 className="auth-title">{isSignup ? 'Create Account' : 'Login to Unlock'}</h3>
        <p className="auth-subtitle">
          {isSignup
            ? 'Create your account with email or continue with Google.'
            : 'Use your Unlock credentials or continue with Google.'}
        </p>

        {alert.message ? (
          <div className={`auth-alert auth-alert-${alert.type}`}>
            <i
              className={`fas ${
                alert.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'
              }`}
            />
            <span>{alert.message}</span>
          </div>
        ) : null}

        {!hasSupabaseConfig ? (
          <div className="auth-alert auth-alert-error">
            <i className="fas fa-exclamation-triangle" />
            <span>Supabase is not configured. Add `REACT_APP_SUPABASE_*` variables.</span>
          </div>
        ) : null}

        <form onSubmit={onSubmit} className="auth-form">
          {isSignup ? (
            <div className="auth-form-group">
              <label htmlFor="auth-username">Username</label>
              <input
                id="auth-username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="Choose a username"
                autoComplete="username"
                disabled={isLoading}
              />
            </div>
          ) : null}

          <div className="auth-form-group">
            <label htmlFor="auth-email">Email</label>
            <input
              id="auth-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              autoComplete="email"
              disabled={isLoading}
            />
          </div>

          <div className="auth-form-group">
            <label htmlFor="auth-password">Password</label>
            <input
              id="auth-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete={isSignup ? 'new-password' : 'current-password'}
              disabled={isLoading}
            />
          </div>

          {isSignup ? (
            <>
              <div className="auth-form-group">
                <label htmlFor="auth-confirm-password">Confirm Password</label>
                <input
                  id="auth-confirm-password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                  disabled={isLoading}
                />
              </div>

              <div className="auth-form-group">
                <label htmlFor="auth-gender">Gender</label>
                <select
                  id="auth-gender"
                  value={gender}
                  onChange={(e) => setGender(e.target.value)}
                  disabled={isLoading}
                >
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                </select>
              </div>

              <div className="auth-checkbox">
                <input
                  id="auth-agree-terms"
                  type="checkbox"
                  checked={agreeToTerms}
                  onChange={(e) => setAgreeToTerms(e.target.checked)}
                  disabled={isLoading}
                />
                <div className="auth-checkbox-text">
                  <span>I agree to the </span>
                  <button
                    type="button"
                    className="auth-inline-link"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      openLegalModal('tos');
                    }}
                    disabled={isLoading}
                  >
                    Terms of Service
                  </button>
                  <span> and </span>
                  <button
                    type="button"
                    className="auth-inline-link"
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                      openLegalModal('privacy');
                    }}
                    disabled={isLoading}
                  >
                    Privacy Policy
                  </button>
                  <span>.</span>
                </div>
              </div>
            </>
          ) : (
            <button
              type="button"
              className="auth-link-button"
              onClick={handleForgotPassword}
              disabled={isLoading}
            >
              Forgot password?
            </button>
          )}

          <button className="auth-submit-btn" type="submit" disabled={isLoading || !hasSupabaseConfig}>
            {isLoading ? (
              <>
                <div className="loading" />
                {isSignup ? 'Creating...' : 'Logging in...'}
              </>
            ) : isSignup ? (
              'Create Account'
            ) : (
              'Login'
            )}
          </button>
        </form>

        <div className="auth-divider">
          <span>or</span>
        </div>

        <button
          type="button"
          className="auth-google-btn"
          onClick={handleGoogleAuth}
          disabled={isLoading || !hasSupabaseConfig}
        >
          <i className="fab fa-google" />
          Continue with Google
        </button>

        <p className="auth-google-note">
          Google sign-in is optional. If this is your first Google login, we&apos;ll ask for a username next.
        </p>

        <button
          type="button"
          className="auth-mode-toggle"
          onClick={() => setIsSignup((prev) => !prev)}
          disabled={isLoading}
        >
          {isSignup ? 'Already have an account? Login' : "Don't have an account? Sign up"}
        </button>
      </div>
      {termsModalVisible ? (
        <div
          className="auth-legal-overlay"
          onClick={(event) => {
            event.stopPropagation();
            setTermsModalVisible(false);
          }}
        >
          <div className="auth-legal-modal" onClick={(event) => event.stopPropagation()}>
            <div className="auth-legal-header">
              <h4>{termsTab === 'tos' ? 'Terms of Service' : 'Privacy Policy'}</h4>
              <button
                type="button"
                className="auth-legal-close-btn"
                onClick={() => setTermsModalVisible(false)}
                aria-label="Close legal content"
              >
                <i className="fas fa-times" />
              </button>
            </div>
            <div className="auth-legal-tabs">
              <button
                type="button"
                className={`auth-legal-tab ${termsTab === 'tos' ? 'active' : ''}`}
                onClick={() => setTermsTab('tos')}
              >
                Terms
              </button>
              <button
                type="button"
                className={`auth-legal-tab ${termsTab === 'privacy' ? 'active' : ''}`}
                onClick={() => setTermsTab('privacy')}
              >
                Privacy
              </button>
            </div>
            <pre className="auth-legal-content">
              {termsTab === 'tos' ? TERMS_OF_SERVICE_CONTENT : PRIVACY_POLICY_CONTENT}
            </pre>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default AuthModal;
